import { useState, useEffect } from 'react';
import { Landmark, Users } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const PARTY_STYLE = {
  D: 'bg-blue-500/10 text-blue-400',
  R: 'bg-red-500/10 text-red-400',
};

export default function CongressionalTrades() {
  const [data, setData] = useState({ trades: [], clusters: [] });
  const [partyFilter, setPartyFilter] = useState('all');

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch('/api/signals/congressional');
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
    }
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  const filtered = data.trades.filter(t =>
    partyFilter === 'all' || t.party === partyFilter
  );

  return (
    <ModuleContainer title="Congressional Trades" subtitle="Politician trading disclosures with relevance scoring">
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        {['all', 'D', 'R'].map(p => (
          <button
            key={p}
            onClick={() => setPartyFilter(p)}
            className={`px-2 py-1 rounded text-[10px] font-mono font-bold ${
              partyFilter === p ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'bg-korinth-bg text-korinth-muted border border-korinth-border'
            }`}
          >
            {p === 'all' ? 'All' : p}
          </button>
        ))}
      </div>

      {/* Cluster alerts */}
      {data.clusters.length > 0 && (
        <div className="mb-4 space-y-1">
          {data.clusters.map((c, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-korinth-warning/10 rounded border border-korinth-warning/20">
              <Users className="w-3 h-3 text-korinth-warning" />
              <span className="text-[10px] font-mono text-korinth-warning">
                <b>{c.politician_count} politicians</b> {c.action.toLowerCase()}d <b>{c.asset}</b> in last 14 days
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Trade list */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {filtered.length > 0 ? filtered.map((t, i) => (
          <div key={t.id || i} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded text-xs font-mono">
            <Landmark className="w-3 h-3 text-korinth-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-white truncate">{t.politician}</span>
                {t.party && <span className={`text-[8px] px-1 py-0.5 rounded font-bold ${PARTY_STYLE[t.party] || ''}`}>{t.party}</span>}
                {t.committee && <span className="text-[8px] px-1 py-0.5 rounded bg-korinth-border text-korinth-muted truncate max-w-[100px]">{t.committee}</span>}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={t.action?.includes('Purchase') ? 'text-korinth-accent' : 'text-korinth-danger'}>
                  {t.action?.includes('Purchase') ? '▲ BUY' : '▼ SELL'}
                </span>
                <span className="text-korinth-text font-bold">{t.asset}</span>
                {t.amount_low && (
                  <span className="text-korinth-muted">${(t.amount_low / 1000).toFixed(0)}K-${(t.amount_high / 1000).toFixed(0)}K</span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              {t.relevance_score != null && (
                <div className="flex items-center gap-1">
                  <div className="w-12 h-1.5 bg-korinth-border rounded-full overflow-hidden">
                    <div className="h-full bg-korinth-accent rounded-full" style={{ width: `${(t.relevance_score / 10) * 100}%` }} />
                  </div>
                  <span className="text-[9px] text-korinth-muted">{t.relevance_score}/10</span>
                </div>
              )}
              <div className="text-[9px] text-korinth-muted mt-0.5">{t.transaction_date?.slice(0, 10)}</div>
            </div>
          </div>
        )) : (
          <p className="text-xs text-korinth-muted font-mono text-center py-6">
            No congressional trades yet. Data fetched every 6 hours.
          </p>
        )}
      </div>
    </ModuleContainer>
  );
}
